<template>
    <span ref="tip" class="info-tip" tabindex="0" :aria-label="text" @mouseenter="place()" @focus="place()">
        <svg-icon icon="ibubble2" />
        <span ref="pop" class="info-tip-pop" :class="above ? 'above' : ''" :style="popStyle" role="tooltip">
            <slot>{{ text }}</slot>
        </span>
    </span>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import SvgIcon from './SvgIcon.vue';

const GAP = 8; // kept between the pop and the edge it would otherwise be cut off at
const WIDTH = 280;

// Inline (i) icon that shows a block of text on hover (or keyboard focus / tap).
// Usage: <info-tip text="..." />, or put richer content in the default slot.
// The pop stays absolute (it scrolls with the page) but is shifted, narrowed or flipped above when it opens
// so it fits inside the nearest scrolling/clipping parent (a modal, say) and the viewport.
export default defineComponent({
    name: 'InfoTip',
    props: ['text'],
    components: {
        SvgIcon
    },
    data() {
        return {
            popStyle: {} as any,
            above: false
        }
    },
    methods: {
        // The visible area: the viewport, cut down by every ancestor that clips its overflow
        bounds(): { left: number, right: number, top: number, bottom: number } {
            const b = { left: 0, right: window.innerWidth, top: 0, bottom: window.innerHeight };
            let el = (this.$refs.tip as HTMLElement).parentElement;
            while(el && el != document.body) {
                const s = getComputedStyle(el);
                if(s.overflowX != 'visible' || s.overflowY != 'visible') {
                    const r = el.getBoundingClientRect();
                    b.left = Math.max(b.left, r.left);
                    b.right = Math.min(b.right, r.right);
                    b.top = Math.max(b.top, r.top);
                    b.bottom = Math.min(b.bottom, r.bottom);
                }
                el = el.parentElement;
            }
            return b;
        },
        place() {
            const tip = (this.$refs.tip as HTMLElement).getBoundingClientRect();
            const b = this.bounds();
            const width = Math.max(160, Math.min(WIDTH, b.right - b.left - GAP * 2));
            // Starts just left of the icon, then slides left until its right edge fits
            let left = tip.left - 10;
            left = Math.min(left, b.right - GAP - width);
            left = Math.max(left, b.left + GAP);
            this.popStyle = { width: width + 'px', maxWidth: 'none', left: (left - tip.left) + 'px' };
            this.$nextTick(() => {
                const height = (this.$refs.pop as HTMLElement).offsetHeight;
                const roomBelow = b.bottom - tip.bottom - GAP;
                const roomAbove = tip.top - b.top - GAP;
                this.above = height + 8 > roomBelow && roomAbove > roomBelow;
            });
        }
    }
});
</script>
